import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import ConferenceBanner from "@/components/ConferenceBanner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/platform", label: "Platform" },
  { to: "/for-buyers", label: "For Buyers" },
  { to: "/for-suppliers", label: "For Suppliers" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-brand-cream/85 backdrop-blur-xl border-b border-brand-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-brand-deep"
                    : "text-brand-muted hover:text-brand-deep"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/get-started"
            data-testid="header-get-started-cta"
            className="group hidden sm:inline-flex items-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream pl-5 pr-3.5 py-2 text-sm font-medium rounded-full transition-colors"
          >
            Get Started
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-cream text-brand-olive transition-transform duration-300 group-hover:translate-x-0.5">
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.2} />
            </span>
          </Link>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-brand-border text-brand-warm hover:text-brand-deep hover:border-brand-muted/50 transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div
          data-testid="mobile-nav-panel"
          className="lg:hidden border-t border-brand-border bg-brand-cream/95 backdrop-blur-xl"
        >
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base tracking-wide transition-colors py-2 ${
                    isActive ? "text-brand-deep" : "text-brand-muted hover:text-brand-deep"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/get-started"
              data-testid="mobile-get-started-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-5 py-3 text-sm font-medium rounded-full transition-colors"
            >
              Get Started
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}

      {/* Live conferences strip — deep forest inverse of the page palette,
          sits directly under the nav bar so it composes with the header height. */}
      <ConferenceBanner />
    </header>
  );
};

export default Header;
